import { CircleCheck } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { formatarData, interpretarNumero } from '@/lib/formato';

import { ondeServe } from './aplicacao';
import {
  explicarFalha,
  useAplicacoes,
  useConversoes,
  useCriarConversao,
  useDesativarConversao,
  useRetirarAplicacao,
  useValidarAplicacao,
} from './consultas';
import { erroDaEmbalagem, erroDoFator, textoDaConversao } from './conversao';
import { opcaoDaConfiguracao, opcaoDoModelo } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * As duas abas do item que o v6 pede no cadastro (módulo 07, ferramenta 1) e
 * que a 0088 trouxe: as UNIDADES em que ele chega (a conversão) e as
 * APLICAÇÕES validadas (em que máquina ele serve). Ficam no painel do item,
 * ao lado dos movimentos: é dali que quem recebe e quem planeja o abrem.
 *
 * Nenhuma ação daqui é laranja: o primário da tela é o da página.
 */


function Esqueleto({ oQue }: { readonly oQue: string }) {
  return (
    <Carregando oQue={oQue} className="flex flex-col gap-3">
      {['w-2/3', 'w-1/2'].map((largura) => (
        <span key={largura} className="flex flex-col gap-2 border-b border-borda-sutil py-2">
          <Barra className={`h-3 ${largura}`} />
          <Barra className="h-2 w-1/3" />
        </span>
      ))}
    </Carregando>
  );
}

/* ====================================================== as unidades do item */

export function AbaDasUnidades({
  produtoId,
  codigo,
  nome,
  unidade,
  podeGerir,
}: {
  readonly produtoId: string;
  readonly codigo: string;
  readonly nome: string;
  readonly unidade: string;
  readonly podeGerir: boolean;
}) {
  const lista = useConversoes(produtoId);
  const criar = useCriarConversao();
  const desativar = useDesativarConversao();
  const [embalagem, setEmbalagem] = useState('');
  const [fator, setFator] = useState('');
  const [tentou, setTentou] = useState(false);

  const erroDaCaixa = tentou ? erroDaEmbalagem(embalagem, unidade) : null;
  const erroDoNumero = tentou ? erroDoFator(fator, unidade) : null;

  const conteudo = lista.isPending ? (
    <Esqueleto oQue="Carregando as unidades do item" />
  ) : lista.isError ? (
    <EstadoVazio
      titulo="Não foi possível carregar as unidades"
      descricao={explicarFalha(lista.error).comoResolver}
      acao={
        <Botao nivel="secundario" onClick={() => { void lista.refetch(); }}>
          Carregar de novo
        </Botao>
      }
    />
  ) : lista.data.length === 0 ? (
    <EstadoVazio
      titulo="Nenhuma unidade de compra"
      descricao={`O item é contado em ${unidade}. Se ele chega em caixa, balde ou fardo, cadastre a embalagem: a entrada e o recebimento convertem para ${unidade}.`}
    />
  ) : (
    <ul className="flex flex-col" aria-label={`Unidades de compra de ${nome}`}>
      {lista.data.map((c) => (
        <li key={c.id} className="flex items-center justify-between gap-4 border-b border-borda-sutil py-2">
          <span className="flex min-w-0 flex-col">
            <span className="txt-corpo text-tinta">{textoDaConversao(c, unidade)}</span>
            <span className="txt-apoio text-tinta-secundaria">{c.ativo === false ? 'Desativada' : 'Ativa'}</span>
          </span>
          {podeGerir && c.ativo !== false ? (
            <Botao
              nivel="fantasma"
              disabled={desativar.isPending}
              onClick={() => {
                desativar.mutate({ id: c.id, produtoId, unidade: c.unidade });
              }}
            >
              Desativar
            </Botao>
          ) : null}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="flex flex-col gap-4">
      {conteudo}
      {podeGerir ? (
        <form
          className="flex flex-col gap-3"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            setTentou(true);
            const n = interpretarNumero(fator);
            if (erroDaEmbalagem(embalagem, unidade) || erroDoFator(fator, unidade) || n === null) return;
            criar.mutate(
              { produtoId, unidade: embalagem.trim(), fator: n, doItem: { codigo, nome, unidade } },
              {
                onSuccess: () => {
                  setEmbalagem('');
                  setFator('');
                  setTentou(false);
                },
              },
            );
          }}
        >
          {criar.isError ? (
            <p role="alert" className="txt-apoio text-parada">
              {`${explicarFalha(criar.error).erro}. ${explicarFalha(criar.error).comoResolver}`}
            </p>
          ) : null}
          <div className="grade-do-painel">
            <CampoTexto rotulo="Embalagem" valor={embalagem} aoMudar={setEmbalagem} placeholder="CX 12" maxLength={40} validar={(v) => erroDaEmbalagem(v, unidade)} />
            <CampoTexto rotulo={`Quantas ${unidade} cabem nela`} valor={fator} aoMudar={setFator} placeholder="12" validar={(v) => erroDoFator(v, unidade)} />
          </div>
          {erroDaCaixa || erroDoNumero ? <p className="txt-apoio text-parada">{erroDaCaixa ?? erroDoNumero}</p> : null}
          <p className="txt-apoio text-tinta-secundaria">O fator não se edita: a unidade errada se desativa e se cadastra outra, e os movimentos antigos ficam com a conta da época.</p>
          <div>
            <Botao nivel="secundario" type="submit" disabled={criar.isPending}>
              {criar.isPending ? 'Cadastrando…' : 'Cadastrar unidade'}
            </Botao>
          </div>
        </form>
      ) : null}
    </div>
  );
}

/* ================================================= as aplicações validadas */


export function AbaDasAplicacoes({
  produtoId,
  nome,
  podeValidar,
}: {
  readonly produtoId: string;
  readonly nome: string;
  readonly podeValidar: boolean;
}) {
  const consulta = useAplicacoes(produtoId);
  const validar = useValidarAplicacao();
  const retirar = useRetirarAplicacao();
  const [modeloId, setModeloId] = useState('');
  const [configuracaoId, setConfiguracaoId] = useState('');
  const [observacao, setObservacao] = useState('');

  const modelos = consulta.data?.modelos ?? [];
  const modelo = modelos.find((m) => m.id === modeloId) ?? null;

  const conteudo = consulta.isPending ? (
    <Esqueleto oQue="Carregando as aplicações do item" />
  ) : consulta.isError ? (
    <EstadoVazio
      titulo="Não foi possível carregar as aplicações"
      descricao={explicarFalha(consulta.error).comoResolver}
      acao={
        <Botao nivel="secundario" onClick={() => { void consulta.refetch(); }}>
          Carregar de novo
        </Botao>
      }
    />
  ) : consulta.data.aplicacoes.length === 0 ? (
    <EstadoVazio
      titulo="Nenhuma aplicação validada"
      descricao="Diga em que modelo ou configuração este item serve. Sem isso, a lista técnica e a peça da O.S. avisam quem o escolhe — avisam, não impedem."
    />
  ) : (
    <ul className="flex flex-col" aria-label={`Aplicações de ${nome}`}>
      {consulta.data.aplicacoes.map((a) => (
        <li key={a.id} className="flex items-center justify-between gap-4 border-b border-borda-sutil py-2">
          {/* R06 (p.71): a aplicação validada com o sinal de conferido. */}
          <CircleCheck aria-hidden="true" className="size-[var(--icone-controle)] shrink-0 text-sucesso" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="txt-corpo text-tinta">{ondeServe(a)}</span>
            <span className="txt-apoio text-tinta-secundaria">
              {[`Validada por ${a.validadoPor ?? 'alguém'} em ${formatarData(new Date(a.validadoEm))}`, a.observacao].filter(Boolean).join(' · ')}
            </span>
          </span>
          {podeValidar ? (
            <Botao
              nivel="fantasma"
              disabled={retirar.isPending}
              onClick={() => {
                retirar.mutate({ id: a.id, produtoId, onde: ondeServe(a) });
              }}
            >
              Retirar
            </Botao>
          ) : null}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="flex flex-col gap-4">
      {conteudo}
      {podeValidar && !consulta.isPending && !consulta.isError ? (
        modelos.length === 0 ? (
          <p className="txt-apoio text-tinta-secundaria">Nenhum modelo de máquina cadastrado. Cadastre o modelo em Equipamentos para validar a aplicação.</p>
        ) : (
          <form
            className="flex flex-col gap-3"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (!modelo) return;
              const config = modelo.configuracoes.find((c) => c.id === configuracaoId) ?? null;
              const onde = ondeServe({ fabricante: modelo.fabricante, modelo: modelo.nome, configuracao: config?.nome ?? null });
              validar.mutate(
                {
                  produtoId,
                  onde,
                  envio: {
                    ...(config ? { configuracaoId: config.id } : { modeloId: modelo.id }),
                    ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    setModeloId('');
                    setConfiguracaoId('');
                    setObservacao('');
                  },
                },
              );
            }}
          >
            {validar.isError ? (
              <p role="alert" className="txt-apoio text-parada">
                {`${explicarFalha(validar.error).erro}. ${explicarFalha(validar.error).comoResolver}`}
              </p>
            ) : null}
            <div className="grade-do-painel">
              <CampoSelecao
                rotulo="Modelo"
                valor={modeloId}
                rotuloVazio="Escolha o modelo"
                opcoes={modelos.map((m) => opcaoDoModelo(m))}
                aoMudar={(v) => {
                  setModeloId(v);
                  setConfiguracaoId('');
                }}
              />
              <CampoSelecao
                rotulo="Configuração"
                opcional
                valor={configuracaoId}
                rotuloVazio="Todas as configurações"
                opcoes={(modelo?.configuracoes ?? []).map((c) => opcaoDaConfiguracao(c, c.nome))}
                aoMudar={setConfiguracaoId}
              />
            </div>
            <CampoTexto rotulo="Como foi conferido" opcional valor={observacao} aoMudar={setObservacao} maxLength={300} placeholder="Catálogo de peças do fabricante, p. 214" />
            <div>
              <Botao nivel="secundario" type="submit" disabled={!modelo || validar.isPending}>
                {validar.isPending ? 'Validando…' : 'Validar aplicação'}
              </Botao>
            </div>
          </form>
        )
      ) : null}
    </div>
  );
}
